import { Card, Stack, styled } from '@mui/material';
import {
  GiftRecipient,
  useGetGiftingPageAndAccountById,
  useGetVssPlanById
} from '@portal-middleware/hooks';
import { Link, Text } from '@vestwell-frontend/ui';

import { FC, useCallback, useEffect, useRef } from 'react';
import { useParams } from 'react-router-dom';

import { useGiftStateContext } from '../contexts/GiftStateContext';

const ImgAutoWidth = styled('img')(({ theme }) => ({
  height: theme.spacing(9),
  width: 'auto'
}));

interface StyledCardProps {
  isActive: boolean;
}

const StyledCard = styled(Card)<StyledCardProps>(({ theme, isActive }) => ({
  borderLeft: `3px solid ${isActive ? theme.palette.primary.main : theme.palette.white}`,
  paddingLeft: `calc(${theme.spacing(4)} - 3px)`
}));

export type RecipientsListProps = {
  giftRecipient: GiftRecipient;
};

const RecipientsLink: FC<RecipientsListProps> = props => {
  const { giftContributorId } = useGiftStateContext();
  const { giftingId } = useParams();

  const linkRef = useRef<HTMLAnchorElement>(null);

  const handleFocus = useCallback(() => {
    if (linkRef.current) {
      linkRef.current.scrollIntoView({
        behavior: 'smooth',
        block: 'nearest'
      });
    }
  }, []);

  useEffect(() => {
    const linkElement = linkRef.current;
    if (linkElement) {
      linkElement.addEventListener('focus', handleFocus);
    }

    return () => {
      if (linkElement) {
        linkElement.removeEventListener('focus', handleFocus);
      }
    };
  }, []);

  const giftAndAccount = useGetGiftingPageAndAccountById(
    props.giftRecipient.encodedAccountId,
    giftContributorId,
    {
      query: {
        suspense: true
      }
    }
  );

  const plan = useGetVssPlanById(
    giftAndAccount?.data?.data?.giftAccount?.planId,
    {
      query: {
        suspense: true
      }
    }
  );

  const isActivePage = giftingId === props.giftRecipient.encodedAccountId;

  useEffect(() => {
    if (isActivePage) {
      linkRef.current.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }
  }, [isActivePage]);

  return (
    <Link
      aria-current={isActivePage ? 'page' : undefined}
      key={props.giftRecipient.id}
      ref={linkRef}
      to={`/gifting/account/${giftAndAccount?.data?.data?.accountPublic?.planId}/${props.giftRecipient.encodedAccountId}`}>
      <StyledCard isActive={isActivePage}>
        <Stack flexDirection='row' gap={2}>
          <ImgAutoWidth
            alt={plan.data?.data?.shortName}
            src={`/assets/images/plan/icon/${plan.data?.data?.id}.svg`}
          />
          <Stack>
            <Text fontWeight={800} mb={0} variant='g2'>
              {giftAndAccount?.data?.data?.accountPublic?.firstName}
            </Text>
            <Text mb={0} variant='j2'>
              {plan?.data?.data?.shortName}
            </Text>
          </Stack>
        </Stack>
      </StyledCard>
    </Link>
  );
};

export default RecipientsLink;
